import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { SellingColumn, SellingItem } from "../../../assets/utils/data/selling";
import { motion } from "framer-motion";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  pointerWithin,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { COLORS } from "../../../assets/utils/enums/colors";
import {
  LabelText,
} from "../../typography";
import ModalWindow from "../modal-window";
import { ActionsEnum } from "../../../assets/utils/enums/settings";
import LayoutCard from "./kanban-board_card";
import SortableColumn from "./kanban-board_column";
import Loader from "../loader";
import normalizeKanbanColumns from "../../../assets/utils/normalized-kanban-columns";
import { ToastTypeEnum } from "../../../assets/utils/enums/toast";
import CustomToast from "../custom-toast";
import toast from "react-hot-toast";
import { authFetch } from "../../../api/authFetch";
import { API_URL_SELLING } from "../../../constants";

/**
 * Повна реалізація:
 * - Перетягування колонок (col-<title>) ⎯ миттєве сортування
 * - Перетягування карток (item-<id>):
 *    • в межах колонки ⎯ миттєве arrayMove
 *    • в іншу колонку ⎯ відкривається модалка підтвердження (після confirm ⎯ перенос)
 * - DragOverlay показує «літаючу» картку під курсором під час drag
 */

export default function KanbanBoard({
  columns,
  setColumns,
}: {
  columns: SellingColumn[];
  setColumns: React.Dispatch<React.SetStateAction<SellingColumn[]>>;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // або 6–8 — залежить від відчуття, 5 оптимально
      },
    })
  );
  const [normalized, setNormalized] = useState<SellingColumn[]>(useMemo(
    () => normalizeKanbanColumns(columns),
    [columns]
  ));
  const [loaderState, setLoaderState] = useState<boolean>(false);

  // Drag overlay ⎯ карта, яку показуємо під курсором під час drag
  const [activeCard, setActiveCard] = React.useState<SellingItem | null>(null);

  // pendingMove ⎯ інформація про картку, яку кинули в іншу колонку і чекає підтвердження
  const [pendingMove, setPendingMove] = React.useState<{
    card: SellingItem;
    fromCol: string;
    toCol: string;
  } | null>(null);

  useEffect(() => {
    setNormalized(normalizeKanbanColumns(columns));
  }, [columns]);


  const findColumnByItemId = (itemId: string) =>
    normalized.find((c) => c.items.some((i) => String(i.id) === itemId));

  const findCardByDragId = (dragId: string): SellingItem | null => {
    const id = String(dragId).replace(/^item-/, "");
    for (const c of normalized) {
      const found = c.items.find((i) => String(i.id) === id);
      if (found) return found;
    }
    return null;
  };

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    if (id.startsWith("item-")) {
      const card = findCardByDragId(id);
      setActiveCard(card);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveCard(null);

    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    // --- переміщення колонок (robust) ---
    if (activeId.startsWith("col-")) {
      if (activeId === overId) return;

      const oldIndex = normalized.findIndex((c) => `col-${c.title}` === activeId);
      const newIndex = normalized.findIndex((c) => `col-${c.title}` === overId);
      if (oldIndex === -1 || newIndex === -1) return;

      setNormalized((prev) => arrayMove(prev, oldIndex, newIndex));

      return;
    }

    // переміщення картки
    if (activeId.startsWith("item-")) {
      const itemId = activeId.replace(/^item-/, "");
      const fromColumn = findColumnByItemId(itemId);
      if (!fromColumn) return;

      let toColumnTitle: string | undefined = undefined;
      if (overId.startsWith("col-")) {
        toColumnTitle = overId.replace(/^col-/, "");
      } else if (overId.startsWith("item-")) {
        const overItemId = overId.replace(/^item-/, "");
        const col = findColumnByItemId(overItemId);
        toColumnTitle = col?.title;
      }

      if (!toColumnTitle) return;

      // якщо в межах тієї ж колонки ⎯ переставляємо без модалки
      if (fromColumn.title === toColumnTitle) {
        const oldIndex = fromColumn.items.findIndex((i) => String(i.id) === itemId);
        const newIndex = fromColumn.items.findIndex((i) => String(i.id) === (overId.startsWith("item-") ? overId.replace(/^item-/, "") : String(i.id)));
        if (oldIndex === -1 || newIndex === -1) return;
        setNormalized((prev) =>
          prev.map((col) =>
            col.title !== fromColumn.title
              ? col
              : { ...col, items: arrayMove(col.items, oldIndex, newIndex) }
          )
        );
        return;
      }

      // якщо це інша колонка ⎯ відкриваємо модалку підтвердження (і НЕ міняємо стан)
      const card = fromColumn.items.find((i) => String(i.id) === itemId)!;
      setPendingMove({ card, fromCol: fromColumn.title, toCol: toColumnTitle });
    }
  };

  const confirmMove = async () => {
    setLoaderState(true);

    if (!pendingMove) return;
    const { card, fromCol, toCol } = pendingMove;

    console.log("Переносимо картку:", card.name, "з", fromCol, "в", toCol);

    setNormalized((prev) => {
      const newCols = prev.map((c) => ({ ...c, items: [...c.items] }));
      const source = newCols.find((c) => c.title === fromCol)!;
      const target = newCols.find((c) => c.title === toCol)!;

      source.items = source.items.filter((i) => i.id !== card.id);

      target.items = [card, ...target.items];

      return newCols;
    });

    try {
      const response = await authFetch(`${API_URL_SELLING}/change-stage?id=${card.id}&stage=${toCol}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!response.ok) {
        toast((t) => (
          <CustomToast
            t={t}
            type={ToastTypeEnum.WARNING}
            text={"Щось пішло не так!"}
          />
        ));
        return;
      }
      toast((t) => (
        <CustomToast
          t={t}
          type={ToastTypeEnum.SUCCESS}
          text={"Картка перенесена!"}
        />
      ));
    } catch (error) {
      console.error("Fetch error:", error);
      toast((t) => (
        <CustomToast
          t={t}
          text={"Упс, щось пішло не так\nПроблема з сервером."}
          type={ToastTypeEnum.ERROR}
        />
      ));
    } finally {
      setTimeout(() => {
        setLoaderState(false);
      }, 150);
    }

    setPendingMove(null);

  };


  const cancelMove = () => {
    setLoaderState(true);
    setPendingMove(null);
    setTimeout(() => {
      setLoaderState(false);
    }, 100);
  };

  if (normalized.length === 0)
    return (
      <LabelText style={{ color: COLORS.secondary1 }}>
        Дані для відображення відсутні
      </LabelText>
    );

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={normalized.map((c) => `col-${c.title}`)} strategy={horizontalListSortingStrategy}>
          <BoardWrapper>
            {normalized.map((column) => (
              <ColumnWrapper key={column.title}>
                <SortableColumn column={column} />
              </ColumnWrapper>
            ))}
          </BoardWrapper>
        </SortableContext>

        {/* DragOverlay ⎯ показує "літаючу" картку під курсором */}
        <DragOverlay dropAnimation={{ duration: 120 }}>
          {activeCard ? (
            <motion.div
              style={{
                zIndex: 1003,
                pointerEvents: "none",
                cursor: "grabbing",
              }}
              initial={{ scale: 1 }}
              animate={{ scale: 1.03, boxShadow: "0 10px 25px rgba(0,0,0,0.28)" }}
              transition={{ duration: 0.12 }}
            >
              <LayoutCard item={activeCard} />
            </motion.div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Модалка підтвердження при переносі між колонками */}
      {pendingMove && (
        <ModalWindow isOpen={true} onConfirm={confirmMove} onCancel={cancelMove} action={ActionsEnum.CONFIRM_KANBAN_MOVING} confirmKanbanCardMoving={
          <>
            Перенести картку <strong>{pendingMove.card.name}</strong> з{" "}{" "}
            <strong>{pendingMove.fromCol}</strong> у{" "}{" "}
            <strong>{pendingMove.toCol}</strong> {" "}?
          </>
        } />
      )}

      {loaderState && <Loader />}
    </>
  );
}

const BoardWrapper = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
height: fit-content;
overflow-x: auto; 
padding: 0px;
padding-bottom: 24px;
  transform: translateZ(0);
  will-change: transform;
  & > * {
    flex: 0 0 auto;
  }

  &::-webkit-scrollbar {
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background-color: ${COLORS["light-bg-color"]};
    border-radius: 100px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${COLORS["light-gray"]};
    border: 1px solid ${COLORS["bg-text-color"]};
    border-radius: 100px;
  }
`;


const ColumnWrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 24px;
  min-width: max-content;
`;